import { createElement } from 'react';
import { siteInfo, categories } from '@/mocks/home';

interface BreadcrumbItem {
  name: string;
  url?: string;
}

export function OrganizationSchema() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    description: siteInfo.description,
    url: 'https://www.naishuidongman.com',
    email: siteInfo.email,
    telephone: siteInfo.phone,
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京市',
      addressRegion: '朝阳区',
      streetAddress: '望京SOHO T1-A座 1508室',
      addressCountry: 'CN',
    },
    sameAs: [
      'https://weibo.com/naishuidongman',
    ],
  };

  return createElement('script', {
    type: 'application/ld+json',
    dangerouslySetInnerHTML: { __html: JSON.stringify(jsonLd) },
  });
}

export function WebSiteSchema() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteInfo.name,
    description: siteInfo.description,
    url: 'https://www.naishuidongman.com',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.naishuidongman.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  return createElement('script', {
    type: 'application/ld+json',
    dangerouslySetInnerHTML: { __html: JSON.stringify(jsonLd) },
  });
}

export function WebPageSchema({ title, description, url }: { title: string; description: string; url: string }) {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description: description,
    url: url,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: 'https://www.naishuidongman.com',
    },
  };

  return createElement('script', {
    type: 'application/ld+json',
    dangerouslySetInnerHTML: { __html: JSON.stringify(jsonLd) },
  });
}

export function BreadcrumbListSchema({ items }: { items: BreadcrumbItem[] }) {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem' as const,
      position: index + 1,
      name: item.name,
      item: item.url || undefined,
    })),
  };

  return createElement('script', {
    type: 'application/ld+json',
    dangerouslySetInnerHTML: { __html: JSON.stringify(jsonLd) },
  });
}

export function ArticleSchema({
  title, description, datePublished, author, imageUrl, url, categoryName,
}: {
  title: string;
  description: string;
  datePublished: string;
  author: string;
  imageUrl: string;
  url: string;
  categoryName: string;
}) {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: description,
    datePublished: datePublished,
    author: {
      '@type': 'Person',
      name: author,
    },
    image: imageUrl,
    url: url,
    articleSection: categoryName,
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
    },
  };

  return createElement('script', {
    type: 'application/ld+json',
    dangerouslySetInnerHTML: { __html: JSON.stringify(jsonLd) },
  });
}

export function FAQPageSchema({ faqs }: { faqs: { question: string; answer: string }[] }) {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question' as const,
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer' as const,
        text: faq.answer,
      },
    })),
  };

  return createElement('script', {
    type: 'application/ld+json',
    dangerouslySetInnerHTML: { __html: JSON.stringify(jsonLd) },
  });
}